import type { BillingDetails, BillingDetailsResponse } from '@app/api/types';
import type { CreditCard } from '@app/api/user';
import { constructUrl } from '@app/api/utils';

import authBase from '../authBase';

export interface TeamInfoResponse {
    seatsTaken: number;
    seatLimit: number;
    freeSeatCount: number;
    defaultCard?: CreditCard;
    teamCreditCents: number;
}

export type AddContributorsValidatePayload = {
    emails: string[];
};

export type AddContributorsValidateResponse = {
    chargeImmediatelyMembers: string[];
    currentGracePeriodMembersCount: number;
    currentInvitedMembersCount: number;
    existingMembers: string[];
    newGracePeriodMembers: string[];
    newInvitedMembers: string[];
    overlimitAllowed: boolean;
    overLimitCount: number;
    teamSeatsTaken: number;
    maxSeatCountBeforeUpgrade: number;
};

export type AddContributorsPayload = {
    emails: string[];
    projectId: string;
    isAdmin: boolean;
    isReviewer: boolean;
    referenceLanguages?: number[];
    contributableLanguages?: number[];
};

export type ProjectLanguage = {
    id: number;
    name: string;
};

export type Project = {
    id: string;
    languages: ProjectLanguage[];
    name: string;
};

export interface PlanLimits {
    keys: number;
    projects: number;
    seats: number;
}

export interface PlanFeature {
    name: string;
    available: boolean;
}

export interface Plan {
    features: Array<PlanFeature>;
    limits: PlanLimits;
    planCost: number;
    planTitle: string;
}

export interface EndOfTrialSuggestionResponse {
    plans: {
        current: Plan;
        recommended: Plan;
    };
    usage: PlanLimits;
}

export type CardSetupIntentResponse = {
    paymentIntentClientSecret: string;
    stripePaymentMethodId: string;
};

export type AddCardResponse = { cardId: number };
export type SetupCardResponse = CardSetupIntentResponse | AddCardResponse;

export const emptyBillingDetails: BillingDetails = {
    company: '',
    address1: '',
    address2: '',
    city: '',
    zip: '',
    phone: '',
    vatnumber: '',
    country_code: '',
    billing_email: '',
    state_code: '',
};

export interface Features {
    [key: number]: string;
}

export enum AllBillingCycles {
    year = 'year',
    month = 'month',
}

export type LimitKey = 'keys' | 'ota' | 'automations' | 'automationsChar';

export enum AllLimitKeys {
    keys = 'keys',
    ota = 'ota',
    automations = 'automations',
    automationsChar = 'automationsChar',
}

export interface SubscriptionPlan {
    planId: number;
    name: string;
    displayName: string;
    period: string;
    periodCount: number;
    price: number;
    priceWithIncludedSeats: number;
    features: number[];
    isSeatBased: boolean;
    minSeatCount: number;
    maxSeatCount: number;
    limits: {
        [limitKey: string]: number;
    };
}

export interface SubscriptionPlans {
    [AllBillingCycles.year]: SubscriptionPlan[];
    [AllBillingCycles.month]: SubscriptionPlan[];
}

export interface UpcomingSubscriptionItem {
    cost: number;
    description: string;
    quantity: number | null; // quantity is null for discount code items
}

export interface UpcomingSubscriptionResponse {
    chargeAmount: number;
    items: readonly UpcomingSubscriptionItem[];
}

export type BillingCycle = 'year' | 'month';

export interface CurrentPlanResponse {
    subscription: {
        planId: number;
        planNickname: string;
        isSeatBased: boolean;
        period: BillingCycle;
        start: string | null;
        end: string | null;
        daysLeft: number | null;
        seatsTaken: number;
        price: number;
        willRenew: boolean;
        status: string | null;
    };
    teamSeatBasedPlanOptions: {
        seatLimit: number;
        freeSeatCount: number;
    };
    canBeModified: boolean;
}

export interface TeamCard {
    brand: string;
    id: number;
    isDefault: boolean;
    last4: string;
    expireMonth: number;
    expireYear: number;
    daysUntilExpire: number;
}

export interface BillingDetailsFormData {
    address1: string;
    billing_email: string;
    city: string;
    zip: string;
    country_code: string;
    state_code?: string;
    company?: string;
    phone?: string;
    address2?: string;
}

export type RoleSetPermissions = {
    admin: boolean;
    biller: boolean;
    member: boolean;
    owner: boolean;
};

export type MemberResponse = {
    canSetRole: RoleSetPermissions;
    email: string;
    fullName: string;
    gracePeriodDaysLeft: number | null;
    gracePeriodStartAt: string | null;
    id: number;
    inviteAcceptDaysLeft: number | null;
    invitedAt: string | null;
    isAdmin: boolean;
    isBiller: boolean;
    isInvisible: boolean;
    isOwner: boolean;
    isSelf: boolean;
    lastLogin: string | null;
    projects: {
        id: string;
        isAdmin: boolean;
        name: string;
    }[];
    userGroups: {
        userGroupId: number;
        name: string;
    }[];
};

export type WholeTeamResponse = {
    content: MemberResponse[];
    count: number;
    filteredTotal: number | null;
    limit: 25 | 50 | 100;
    offset: number;
    total: number;
};

export type ChurnReason =
    | 'one-off-project'
    | 'too-expensive'
    | 'too-complicated'
    | 'missing-features'
    | 'localisation-no-longer-relevant'
    | 'technical-issues'
    | 'not-using-enough'
    | 'other';

export type ChurnNextStep =
    | 'no-solution-needed-at-all'
    | 'no-solution-needed-now'
    | 'moving-to-competitor'
    | 'switching-to-in-house'
    | 'other';

export interface CancelSubscriptionRequest {
    churnReason: ChurnReason;
    churnDetails?: string;
    nextStep: ChurnNextStep;
    competitor?: string;
}

export type SubscribePaymentSetupResponse = {
    stripePaymentMethodId: string;
    paymentIntentClientSecret: string;
};

export type SubscribeSuccessResponse = {
    result: string;
};

export type SubscribeResponse = SubscribePaymentSetupResponse | SubscribeSuccessResponse;
export type DesignPlugins = 'adobe' | 'figma' | 'sketch';

export type DesignPluginsTrialPayload =
    | {
          teamId: string;
          status: 'activated';
          designPluginName: DesignPlugins;
      }
    | {
          teamId: string;
          status: 'not_interested';
          designPluginName?: undefined;
      };

export type UserWithStats = {
    email: string;
    fullname: string;
    id: string;
    user_team_id: string;
};

export type BillingHistoryItem = {
    id: string;
    orderId: string;
    createdAt: string;
    note: string;
    amountCents: string;
    item: string;
};

export type BillingHistoryResponse = {
    offset: number;
    count: number;
    total: number;
    filteredTotal: null;
    limit: number;
    content: BillingHistoryItem[];
};

export type BillingHistoryGeneralResponse = {
    creditSpentAmountCents: number;
    freeOrderAmountCents: number;
    existingProcesses: boolean;
};

export type UpdateUserPayload = {
    memberId: number;
    role: string;
    teamId: string;
};

type RemoveUserPayload = {
    memberId: number;
    teamId: string;
};

type GetAllTeamMembersPayload = {
    teamId: string;
    page: number;
    pageSize: number;
    search: string;
};

export type TeamUserGroup = {
    id: number;
    avatar: string;
    name: string;
    allEditableLanguages: boolean;
    allReadOnlyLanguages: boolean;
    editableLanguages: {
        id: string;
        name: string;
        isoCode: string;
    }[];
    readOnlyLanguages: {
        id: string;
        name: string;
        isoCode: string;
    }[];

    projects: {
        id: string;
        name: string;
    }[];

    roleId: number | null;
    permissions: {
        id: string;
        title: string;
        checked: boolean;
        enabled: boolean;
        scope: string;
        group?: PermissionGroupName;
    }[];

    contributors: {
        id: number;
        name: string;
        email: string;
        avatar: string;
        groups: {
            id: string;
            name: string;
        }[];
    }[];
};

type GetUserGroupsRequest = {
    teamId: string;
    p?: string;
    q?: string;
    l?: string[];
    permissions?: string[];
};

type GetUserGroupDetailsRequest = {
    userGroupId: number;
};

export type PermissionId =
    | 'tasks'
    | 'review'
    | 'upload'
    | 'activity'
    | 'download'
    | 'settings'
    | 'statistics'
    | 'manage_keys'
    | 'screenshots'
    | 'contributors'
    | 'glossary_edit'
    | 'branches_merge'
    | 'branches_create'
    | 'glossary_delete'
    | 'manage_languages'
    | 'branches_main_modify'
    | 'custom_status_modify';

export type PermissionGroupName =
    | 'Content'
    | 'Activity'
    | 'Review'
    | 'Content transfer'
    | 'Project'
    | 'Branches'
    | 'Glossary';

type SaveUserGroupPayload = {
    userGroupId: number;
    roleId: number | null;
    permissions: PermissionId[];
    readableLanguageIds: number[];
    editableLanguageIds: number[];
    isAllLanguagesReadable: boolean;
    isAllLanguagesEditable: boolean;
};

// NOTE: this DTO is for a legacy endpoint and will be removed in the future
// This is why all fot he eslint disable comments are here
export type UserGroupDetailsResponse = {
    id: number;
    name: string;
    team_id: number;
    logo_url: string;
    created_at: Date;

    role_id: number | null;
    permissions: Record<PermissionId, boolean>;
    members: {
        id: number;
        fullname: string;
        email: string;
        user_group_id: number;
        is_invisible: boolean;
    }[];
    projects: {
        id: string;
        name: string;
    }[];

    all_languages_readonly: 1 | 0;
    all_languages_editable: 1 | 0;
    languages: {
        contributable: {
            user_group_id: number;
            lang_id: string;
            is_editable: boolean;
            name_en_US: string;
            lang_iso: string;
        }[];
        reference: {
            user_group_id: number;
            lang_id: string;
            is_editable: boolean;
            name_en_US: string;
            lang_iso: string;
        }[];
    };
};

export const teamApi = authBase.injectEndpoints({
    endpoints: (builder) => ({
        getTeamUserGroups: builder.query<TeamUserGroup[], GetUserGroupsRequest>({
            query: ({ teamId, ...params }) => ({
                method: 'GET',
                url: constructUrl(`/team/beta/${teamId}/user-groups`, { ...params }),
            }),
            providesTags: (groups) =>
                groups ? [...groups.map((group) => ({ type: 'userGroups' as const, id: group.id }))] : [],
        }),
        getFeatures: builder.query<Features, void>({
            query: () => ({
                method: 'GET',
                url: '/features.json',
            }),
        }),
        getSubscriptionPlans: builder.query<SubscriptionPlans, void>({
            query: () => ({
                method: 'GET',
                url: '/subscription-plans',
            }),
            transformResponse: (response: SubscriptionPlans) => ({
                month: response[AllBillingCycles.month].sort((a, b) => a.planId - b.planId),
                year: response[AllBillingCycles.year].sort((a, b) => a.planId - b.planId),
            }),
        }),
        getTeamBillingDetails: builder.query<BillingDetails, void>({
            query: () => ({
                method: 'GET',
                url: '/team/billing-details',
            }),
            // Empty array is returned from our BE when there is no data.
            // see https://lokalise.atlassian.net/browse/GROW-665 for details.
            transformResponse: (response: BillingDetailsResponse) => {
                if (Array.isArray(response)) {
                    return emptyBillingDetails;
                }

                return response;
            },
        }),
        updateBillingDetails: builder.mutation<BillingDetailsResponse, BillingDetailsFormData>({
            query: (data) => ({
                method: 'PUT',
                url: '/team/billing-details',
                body: data,
            }),
        }),
        getTeamInfo: builder.query<TeamInfoResponse, void>({
            query: () => ({
                method: 'GET',
                url: '/team/info',
            }),
        }),
        getBillingHistoryGeneral: builder.query<BillingHistoryGeneralResponse, void>({
            query: () => ({
                method: 'GET',
                url: '/team/billing-history/general',
            }),
        }),
        getBillingHistory: builder.query<BillingHistoryResponse, { page: number; pageSize: number }>({
            query: ({ page, pageSize }) => ({
                method: 'GET',
                url: `/team/billing-history?page=${page}&pageSize=${pageSize}`,
            }),
        }),
        createTeam: builder.mutation<{ id: number }, { team_name: string }>({
            query: (data) => ({
                method: 'POST',
                url: `/team/create`,
                body: data,
            }),
        }),
        switchTeam: builder.mutation<void, { id: number }>({
            query: (data) => ({
                method: 'POST',
                url: `/team/switch`,
                body: data,
            }),
        }),
        upcomingTeamSubscription: builder.query<UpcomingSubscriptionResponse, { seatCount: number; planId: number }>({
            query: ({ seatCount, planId }) => ({
                method: 'GET',
                url: '/team/subscription/plan/upcoming',
                params: { seatCount, planId },
            }),
        }),
        newUpcomingTeamSubscription: builder.query<UpcomingSubscriptionResponse, { seatCount: number; planId: number }>(
            {
                query: ({ seatCount, planId }) => ({
                    method: 'GET',
                    url: '/team/subscription/plan/new/upcoming',
                    params: { seatCount, planId },
                }),
            },
        ),
        resumeSubscription: builder.mutation<void, void>({
            query: () => ({
                method: 'POST',
                url: `/team/subscription/resume`,
            }),
        }),
        cancelSubscription: builder.mutation<void, CancelSubscriptionRequest>({
            query: (body) => ({
                method: 'POST',
                url: `/team/subscription/cancel`,
                body,
            }),
        }),
        getEndOfTrialSuggestion: builder.query<EndOfTrialSuggestionResponse, void>({
            query: () => ({
                method: 'GET',
                url: '/team/end-of-trial/suggestion',
            }),
        }),
        teamDowngrade: builder.mutation<{ result: string }, { reason: string }>({
            query: (data) => ({
                method: 'POST',
                url: '/team/end-of-trial/downgrade',
                body: data,
            }),
        }),
        editName: builder.mutation<{ err: string }, { team_id: string; name: string }>({
            query: (data) => ({
                method: 'POST',
                url: '/team/edit-name',
                body: data,
            }),
        }),
        getProjectsList: builder.query<Project[], void>({
            query: () => ({
                method: 'GET',
                url: '/team/contributors/list-projects',
            }),
        }),
        addContributors: builder.mutation<void, AddContributorsPayload>({
            query: (payload) => ({
                method: 'POST',
                url: '/team/contributors/add',
                body: payload,
            }),
            invalidatesTags: ['teamContributors'],
        }),
        getCurrentTeamPlan: builder.query<CurrentPlanResponse, void>({
            query: () => ({
                method: 'GET',
                url: '/team/current-plan',
            }),
        }),
        getTeamCards: builder.query<TeamCard[], void>({
            query: () => ({
                method: 'GET',
                url: '/team/subscription/cards',
            }),
            providesTags: ['cards'],
        }),
        subscribe: builder.mutation<SubscribeResponse, { paymentMethodId: number; seatCount: number; planId: number }>({
            query: (payload) => ({
                method: 'POST',
                url: '/team/subscription/subscribe',
                body: payload,
            }),
        }),
        addContributorsValidate: builder.mutation<AddContributorsValidateResponse, AddContributorsValidatePayload>({
            query: (payload) => ({
                method: 'POST',
                url: '/team/contributors/add/validate',
                body: payload,
            }),
        }),
        addTeamCard: builder.mutation<SetupCardResponse, { id: string; laterUse: boolean }>({
            query: ({ id, laterUse }) => ({
                method: 'POST',
                url: '/team/subscription/add-card',
                params: {
                    token_id: id,
                    later_use: laterUse ? 1 : 0,
                },
            }),
            invalidatesTags: ['cards'],
        }),
        addMembersToGroup: builder.mutation<void, { groupId: number; emails: string[] }>({
            query: (payload) => ({
                method: 'POST',
                url: '/team/user-group/add-members',
                body: payload,
            }),
        }),
        saveUserGroup: builder.mutation<void, SaveUserGroupPayload>({
            query: (body) => ({
                method: 'POST',
                url: `/team/user-group/beta/save`,
                body,
            }),
            invalidatesTags: ['userGroups'],
        }),
        getUserGroupsDetails: builder.query<UserGroupDetailsResponse, GetUserGroupDetailsRequest>({
            query: ({ userGroupId }) => ({
                method: 'GET',
                url: `/team/user-group/get/${userGroupId}`,
            }),
            providesTags: ['userGroups'],
        }),
        switchTeamCard: builder.mutation<void, { cardId: string }>({
            query: ({ cardId }) => ({
                method: 'POST',
                url: '/team/subscription/switch-card',
                params: {
                    card_id: cardId,
                },
            }),
        }),
        deleteTeamCard: builder.mutation<void, { cardId: string }>({
            query: ({ cardId }) => ({
                method: 'DELETE',
                url: `/team/subscription/card/${cardId}`,
            }),
        }),
        activateDesignPluginsTrial: builder.mutation<void, DesignPluginsTrialPayload>({
            query: ({ teamId, status, designPluginName }) => ({
                method: 'PUT',
                url: `/teams/${teamId}/design-plugins-trials`,
                body: {
                    status,
                    designPluginName,
                },
            }),
        }),
        deleteTeam: builder.mutation<void, { teamName: string; teamId: string }>({
            query: ({ teamName, teamId }) => ({
                method: 'POST',
                url: '/team/destroy',
                params: {
                    team_name: teamName,
                    team_id: teamId,
                },
            }),
        }),
        getUsersWithReportStats: builder.query<UserWithStats[], void>({
            query: () => ({
                method: 'GET',
                url: '/team/members/users-with-stats',
            }),
        }),
        updateUser: builder.mutation<void, UpdateUserPayload>({
            query: ({ memberId, role, teamId }) => ({
                method: 'PATCH',
                url: `/teams/${teamId}/members/${memberId}/role`,
                body: { role },
            }),
            invalidatesTags: ['teamContributors'],
        }),
        removeUser: builder.mutation<void, RemoveUserPayload>({
            query: ({ memberId, teamId }) => ({
                method: 'delete',
                url: `/teams/${teamId}/members/${memberId}`,
            }),
            invalidatesTags: ['teamContributors'],
        }),
        getAllTeamMembers: builder.query<WholeTeamResponse, GetAllTeamMembersPayload>({
            query: ({ teamId, page, pageSize, search }) => ({
                method: 'GET',
                url: `/teams/${teamId}/members`,
                params: {
                    page,
                    pageSize,
                    search,
                },
            }),
            providesTags: ['teamContributors'],
        }),
    }),
});

export const {
    useGetTeamUserGroupsQuery,
    useLazyGetTeamUserGroupsQuery,
    useGetSubscriptionPlansQuery,
    useGetFeaturesQuery,
    useGetTeamBillingDetailsQuery,
    useGetTeamInfoQuery,
    useCreateTeamMutation,
    useSwitchTeamMutation,
    useUpcomingTeamSubscriptionQuery,
    useNewUpcomingTeamSubscriptionQuery,
    useResumeSubscriptionMutation,
    useCancelSubscriptionMutation,
    useGetEndOfTrialSuggestionQuery,
    useTeamDowngradeMutation,
    useEditNameMutation,
    useAddContributorsMutation,
    useGetProjectsListQuery,
    useGetCurrentTeamPlanQuery,
    useGetTeamCardsQuery,
    useSubscribeMutation,
    useUpdateBillingDetailsMutation,
    useAddTeamCardMutation,
    useAddContributorsValidateMutation,
    useAddMembersToGroupMutation,
    useSwitchTeamCardMutation,
    useDeleteTeamCardMutation,
    useActivateDesignPluginsTrialMutation,
    useGetUsersWithReportStatsQuery,
    useDeleteTeamMutation,
    useGetBillingHistoryGeneralQuery,
    useGetBillingHistoryQuery,
    useUpdateUserMutation,
    useRemoveUserMutation,
    useGetAllTeamMembersQuery,
    useSaveUserGroupMutation,
    useGetUserGroupsDetailsQuery,
} = teamApi;
